import type { FC } from 'react';
import { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';

import { isVideogameWithRounds } from 'app/config/videogameSpecific.config.helper';
import { withBackgroundImage } from 'app/utils/style';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';
import ConfigContext from 'app/common/ConfigContext';
import GameTimer from 'app/components/GameTimer';
import IconWithTooltip from 'app/components/IconWithTooltip';
import LiveTag from 'app/components/LiveTag';
import MapNameTag from 'app/components/MapNameTag';
import TeamName from 'app/components/TeamName';
import { formatGoldLead, getMap } from 'app/utils/entities/match_data';
import { localizedMapName } from 'app/i18n/utils';
import StatValue from 'app/components/StatValue';
import Dota2KillIcon from 'app/icons/dota2_kill.svg';
import VideogameVersionTag from 'app/components/VideogameVersionTag';
import Dota2LiveScoreboard from 'app/components/Blocks/NameBlock/Dota2LiveScoreboard';
import GoldAdvantageIcon from 'app/icons/gold_advantage.svg';
import useWindowSize from 'app/hooks/useWindowSize';
import { round } from 'app/utils/number';
import DisplayContext from 'app/common/DisplayContext';
import { useEmblaCarouselDotButtons } from 'app/hooks/useEmblaCarouselDotButtons';
import { partitionArray } from 'app/utils/array';
import GoldAdvantageChart from 'app/components/GoldAdvantageChart';
import { getScoreboardTypeForVideogame } from 'app/config/urlParams.config.helper';
import DoubleArrowIcon from 'app/icons/arrow-double.svg';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';
import {
  getFinalDota2GoldAdvantage,
  getGameStat,
  getLiveStats,
  getNumberOfGameWon,
  getScoreboardGame,
  isMatchLive
} from './utils';

interface Props {
  data: NameBlockMatch;
}

const MatchScore: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { scoreboard, user } = useContext(ConfigContext);
  const { singleBlockDisplay, displayedBlocks } = useContext(DisplayContext);

  const scoreboardType = getScoreboardTypeForVideogame(scoreboard, data.videogame_slug);

  const minimapScoreboard = scoreboardType === 'minimap';

  const minimapScoreboardControl = user === 'winline' ? 'buttons' : 'swipe';

  const [emblaRef, emblaApi] = useEmblaCarousel({
    active: minimapScoreboard,
    watchDrag: minimapScoreboardControl === 'swipe'
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useEmblaCarouselDotButtons(emblaApi);

  const scoreboardGame = getScoreboardGame(data);
  const gameMap = getMap(scoreboardGame);

  const isLive = isMatchLive(data);

  const opponent1 = getGameStat(scoreboardGame, data.opponents.at(0)?.id);
  const opponent2 = getGameStat(scoreboardGame, data.opponents.at(1)?.id);

  const opponent1NumberOfGameWins = getNumberOfGameWon(data.games, data.opponents.at(0)?.id);
  const opponent2NumberOfGameWins = getNumberOfGameWon(data.games, data.opponents.at(1)?.id);

  const roundsDisplay = isVideogameWithRounds(data.videogame_slug);

  const liveStats = getLiveStats(data.videogame_slug, opponent1, opponent2);

  const [goldAdvantageLastValue, goldAdvantageSide] = getFinalDota2GoldAdvantage(
    scoreboardGame?.radiant_gold_adv
  );

  const opponent1Kills = (opponent1 && 'kills' in opponent1 && opponent1.kills) || null;
  const opponent2Kills = (opponent2 && 'kills' in opponent2 && opponent2.kills) || null;

  const opponent1RoundScore = (opponent1 && 'score' in opponent1 && opponent1.score) || null;
  const opponent2RoundScore = (opponent2 && 'score' in opponent2 && opponent2.score) || null;

  const { width } = useWindowSize();

  const scale =
    width <= 280 && minimapScoreboard ? { transform: `scale(${round(width / 280, 2)})` } : {};

  // Dota specific code
  const [dires, _] = partitionArray(scoreboardGame?.opponents ?? [], opponent =>
    opponent.side === 'dire' ? 0 : 1
  );
  const dota2opponent1 = data.opponents.at(0);
  const dota2opponent2 = data.opponents.at(1);
  const dire = dires.at(0);
  const [direOpponentInfo, radiantOpponentInfo] =
    dota2opponent1?.id === dire?.id
      ? [dota2opponent1, dota2opponent2]
      : [dota2opponent2, dota2opponent1];

  const buttonsControl =
    minimapScoreboard && scoreboardGame && minimapScoreboardControl === 'buttons';

  return (
    <BlockTemplate
      className={classNames('override-block_name', {
        [style.matchScoreBlockTemplateWithMap]:
          scoreboardGame && gameMap && scoreboardType === 'basic',
        [style.fullHeightDisplay]:
          displayedBlocks.length === 1 && displayedBlocks[0] === 'name' && !singleBlockDisplay,
        [style.roundsDisplay]: roundsDisplay,
        [style.minimapScoreboard]: minimapScoreboard
      })}
      containerClassname={classNames(style.blockNameContent, {
        [style.minimapScoreboardContent]: minimapScoreboard
      })}
    >
      <div ref={emblaRef} className={style.emblaRef}>
        <div className={style.emblaCarouselContainer}>
          <div className={style.emblaCarouselSlide}>
            <div
              className={classNames(style.matchScoreBlockContainer, {
                [style.withSlideButton]: buttonsControl
              })}
              style={scale}
            >
              {scoreboardType !== 'minimap' ? (
                <>
                  <div
                    className={classNames(style.infoContainer, {
                      [style.infoContainerWithMap]: gameMap && roundsDisplay
                    })}
                  >
                    <div className={style.liveMatchHeader}>
                      <h1 className={style.serieName}>
                        {data.league_name} {data.serie_full_name} {data.tournament_name}{' '}
                        <VideogameVersionTag name={data.videogame_title} />
                      </h1>
                      {isLive && <LiveTag />}
                    </div>

                    <div
                      className={classNames(style.teamsScore, {
                        [style.roundsDisplay]: roundsDisplay
                      })}
                    >
                      <div className={style.teamScoreLine}>
                        <TeamName
                          team={data.opponents.at(0)}
                          logoStyle={style.teamScoreNameLogo}
                          className={style.teamScoreName}
                        />
                        <div
                          className={classNames(style.numberOfGameWin, {
                            [style.lowestScore]:
                              opponent1NumberOfGameWins < opponent2NumberOfGameWins
                          })}
                        >
                          {opponent1NumberOfGameWins}
                        </div>
                        {roundsDisplay && (
                          <div
                            className={classNames(
                              style.gameScore,
                              style[opponent1?.side ?? 'defaultColor']
                            )}
                          >
                            {opponent1RoundScore ?? 0}
                          </div>
                        )}
                      </div>
                      {!roundsDisplay && <div className={style.vs}>{t('all-versus')}</div>}
                      <div className={style.teamScoreLine}>
                        <TeamName
                          team={data.opponents.at(1)}
                          logoStyle={style.teamScoreNameLogo}
                          className={style.teamScoreName}
                          orientation={roundsDisplay ? 'normal' : 'reversed'}
                        />
                        <div
                          className={classNames(style.numberOfGameWin, {
                            [style.lowestScore]:
                              opponent2NumberOfGameWins < opponent1NumberOfGameWins
                          })}
                        >
                          {opponent2NumberOfGameWins}
                        </div>
                        {roundsDisplay && (
                          <div
                            className={classNames(
                              style.gameScore,
                              style[opponent2?.side ?? 'defaultColor']
                            )}
                          >
                            {opponent2RoundScore ?? 0}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {scoreboardGame?.timer && (
                    <GameTimer
                      timer={scoreboardGame.timer}
                      videogame={scoreboardGame.videogame_slug}
                      live={scoreboardGame.status !== 'finished'}
                      className={style.timer}
                    />
                  )}

                  {scoreboardGame && gameMap && (
                    <div
                      style={withBackgroundImage(getCDNUrl(gameMap.image_url))}
                      className={classNames(style.map, {
                        [style.mapWithBorderRadius]: singleBlockDisplay,
                        [style.roundsDisplay]: roundsDisplay
                      })}
                    >
                      <div className={style.teamStatsContainer}>
                        {roundsDisplay && <MapNameTag name={localizedMapName(gameMap, t)} />}
                        {liveStats.opponent1MatchStats.length > 0 && (
                          <ul className={style.teamStats}>
                            {liveStats.opponent1MatchStats.map(stat => (
                              <li key={`opponent1_${stat.name}`}>
                                {stat.Icon && (
                                  <IconWithTooltip
                                    Icon={stat.Icon}
                                    label=""
                                    className={style.teamStatsTooltip}
                                    iconClassName={classNames(
                                      style.teamStatIcon,
                                      style[opponent1?.side ?? 'defaultColor']
                                    )}
                                  />
                                )}
                                <StatValue
                                  value={stat.value}
                                  displayFunction={stat.displayFunction}
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                        {!roundsDisplay &&
                          !!scoreboardGame?.opponents.length &&
                          data.videogame_slug === 'league-of-legends' && (
                            <div className={style.killsContainer}>
                              {opponent1Kills ?? 0}
                              <IconWithTooltip
                                Icon={Dota2KillIcon}
                                label={t('all-kills')}
                                iconClassName={classNames(style.killsIcon, {
                                  [style.reversed]: (opponent1Kills || 0) > (opponent2Kills || 0)
                                })}
                              />
                              {opponent2Kills ?? 0}
                            </div>
                          )}

                        {goldAdvantageLastValue && (
                          <div
                            className={classNames(
                              style.goldAdvantageContainer,
                              style[goldAdvantageSide]
                            )}
                          >
                            <GoldAdvantageIcon />
                            {formatGoldLead(goldAdvantageLastValue, 'exact', false)}
                          </div>
                        )}
                        {liveStats.opponent2MatchStats.length > 0 && (
                          <ul className={classNames(style.teamStats, style.reversed)}>
                            {liveStats.opponent2MatchStats.map(stat => (
                              <li key={`opponent1_${stat.name}`}>
                                {stat.Icon && (
                                  <IconWithTooltip
                                    Icon={stat.Icon}
                                    label=""
                                    className={style.teamStatsTooltip}
                                    iconClassName={classNames(
                                      style.teamStatIcon,
                                      style[opponent2?.side ?? 'defaultColor']
                                    )}
                                  />
                                )}
                                <StatValue
                                  value={stat.value}
                                  displayFunction={stat.displayFunction}
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={style.minimapScoreboardInfoContainer}>
                  <div
                    className={classNames(
                      style.minimapScoreboardTeamStats,
                      style[opponent1?.side ?? 'defaultColor']
                    )}
                  >
                    <div className={style.goldAdvantage}>
                      {goldAdvantageLastValue && goldAdvantageSide === opponent1?.side && (
                        <div
                          className={classNames(
                            style.goldAdvantageContainer,
                            style[goldAdvantageSide]
                          )}
                        >
                          <GoldAdvantageIcon className={style.goldAdvantageIcon} />
                          {formatGoldLead(goldAdvantageLastValue, 'abbreviated', false)}
                        </div>
                      )}
                    </div>
                    <div
                      className={classNames(style.numberOfGameWin, {
                        [style.lowestScore]: opponent1NumberOfGameWins < opponent2NumberOfGameWins
                      })}
                    >
                      {opponent1NumberOfGameWins}
                    </div>
                    <TeamName
                      team={data.opponents.at(0)}
                      logoStyle={style.teamScoreNameLogo}
                      className={style.teamScoreName}
                      useAcronym
                    />
                    <ul
                      className={classNames(
                        style.teamStats,
                        style[opponent1?.side ?? 'defaultColor']
                      )}
                    >
                      {liveStats.opponent1MatchStats.map(stat => (
                        <li key={`opponent1_${stat.name}`}>
                          {stat.Icon && (
                            <IconWithTooltip
                              Icon={stat.Icon}
                              label=""
                              className={style.teamStatsTooltip}
                              iconClassName={classNames(style.teamStatIcon)}
                            />
                          )}
                          <StatValue value={stat.value} displayFunction={stat.displayFunction} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={classNames(
                      style.minimapScoreboardTeamStats,
                      style[opponent2?.side ?? 'defaultColor']
                    )}
                  >
                    <div
                      className={classNames(style.numberOfGameWin, {
                        [style.lowestScore]: opponent2NumberOfGameWins < opponent1NumberOfGameWins
                      })}
                    >
                      {opponent2NumberOfGameWins}
                    </div>
                    <TeamName
                      team={data.opponents.at(1)}
                      logoStyle={style.teamScoreNameLogo}
                      className={style.teamScoreName}
                      useAcronym
                    />
                    <ul
                      className={classNames(
                        style.teamStats,
                        style[opponent2?.side ?? 'defaultColor']
                      )}
                    >
                      {liveStats.opponent2MatchStats.map(stat => (
                        <li key={`opponent2_${stat.name}`}>
                          {stat.Icon && (
                            <IconWithTooltip
                              Icon={stat.Icon}
                              label=""
                              className={style.teamStatsTooltip}
                              iconClassName={classNames(style.teamStatIcon)}
                            />
                          )}
                          <StatValue value={stat.value} displayFunction={stat.displayFunction} />
                        </li>
                      ))}
                    </ul>
                    <div className={style.goldAdvantage}>
                      {goldAdvantageLastValue && goldAdvantageSide === opponent2?.side && (
                        <div
                          className={classNames(
                            style.goldAdvantageContainer,
                            style[goldAdvantageSide]
                          )}
                        >
                          <GoldAdvantageIcon className={style.goldAdvantageIcon} />
                          {formatGoldLead(goldAdvantageLastValue, 'abbreviated', false)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className={style.liveScoreboardContainer}>
                {scoreboardType === 'minimap' && scoreboardGame?.timer && (
                  <GameTimer
                    timer={scoreboardGame.timer}
                    videogame={scoreboardGame.videogame_slug}
                    live={scoreboardGame.status !== 'finished'}
                    className={style.timer}
                  />
                )}
                {data.videogame_slug === 'dota-2' && scoreboardGame && (
                  <Dota2LiveScoreboard
                    game={scoreboardGame as LiveGameDota2}
                    opponent1={data.opponents.at(0)}
                    opponent2={data.opponents.at(1)}
                    matchStatus={data.match_status}
                  />
                )}
              </div>
              {buttonsControl && (
                <button
                  type="button"
                  onClick={() => emblaApi?.scrollNext()}
                  className={style.emblaCarouselNextButton}
                >
                  <DoubleArrowIcon className={style.arrowIcon} />
                </button>
              )}
            </div>
          </div>
          {minimapScoreboard && scoreboardGame && (
            <div className={style.emblaCarouselSlide}>
              <div className={style.minimapScoreboardChartContainer} style={scale}>
                {buttonsControl && (
                  <button
                    type="button"
                    onClick={() => emblaApi?.scrollPrev()}
                    className={style.emblaCarouselPrevButton}
                  >
                    <DoubleArrowIcon className={style.arrowIcon} />
                  </button>
                )}
                <div className={style.minimapScoreboardChartHeader}>
                  <div className={style.minimapScoreboardChartTeams}>
                    <div className={style.minimapScoreboardChartTeam}>
                      <div className={classNames(style.minimapScoreboardChartSide, style.dire)}>
                        Dire
                      </div>
                      <TeamName team={direOpponentInfo} useAcronym />
                    </div>
                    <div className={style.minimapScoreboardChartTeam}>
                      <div className={classNames(style.minimapScoreboardChartSide, style.radiant)}>
                        Radiant
                      </div>
                      <TeamName team={radiantOpponentInfo} useAcronym />
                    </div>
                  </div>
                  {scoreboardGame && scoreboardGame.timer && (
                    <GameTimer
                      timer={scoreboardGame.timer}
                      videogame={scoreboardGame.videogame_slug}
                      live={scoreboardGame.status !== 'finished'}
                      className={style.chartTimer}
                    />
                  )}
                </div>
                <GoldAdvantageChart
                  goldAdvantageTeamInfo={radiantOpponentInfo}
                  secondTeamInfo={direOpponentInfo}
                  goldAvantageTeam="radiant"
                  goldAdvantage={scoreboardGame.radiant_gold_adv ?? []}
                  display="dota-2-scoreboard"
                  className={classNames(style.minimapScoreboardChart, {
                    [style.withSlideButton]: minimapScoreboardControl === 'buttons'
                  })}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {minimapScoreboard && scoreboardGame && minimapScoreboardControl === 'swipe' && (
        <div className={style.emblaCarouselDotButtons}>
          {scrollSnaps.map((_, index) => (
            <button
              type="button"
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={classNames(style.emblaCarouselDot, {
                [style.emblaCarouselDotSelected]: index === selectedIndex
              })}
              aria-label="scoreboard view selector"
            />
          ))}
        </div>
      )}
    </BlockTemplate>
  );
};

MatchScore.displayName = 'MatchScore';

export default MatchScore;
